import '../styles/demo-dock.css';
import { useEffect, useRef, useState } from 'react';
import { useDemo } from './Demo.tsx';
import { useReducedMotion } from '../motion.ts';

/**
 * The Ask bar — the page's way into the product.
 *
 * This was a small card in the corner that said "Ask me anything about
 * Persivo." It read as a chat widget, which is the thing this product is not, and
 * it was too small to be the product's front door. It is now the shape every
 * good "ask" launcher has settled on — a bar you can see is for typing, with a
 * question already half-written in it, a key to reach it, and suggestions that
 * open when you reach for it — and it does what only this product can: whatever
 * you type is spoken as your own first line, and the guide answers by *showing*
 * you, on the product, in the frame that opens from the bar itself.
 *
 *  - It is earned, not thrown: it rises after a few seconds or the first scroll,
 *    never on load, and it is not on the page while the pilot form is.
 *  - Over the pinned scenes (the story, how it works, the Studio stage), where
 *    the pictures reach the bottom of the screen, it folds to a small "Ask
 *    Persivo" pill and opens again when you reach for it.
 *  - ⌘K / Ctrl+K, or "/", reaches it from anywhere. The ✕ folds it to the pill
 *    for the session — it is never removed, because the front door should not be
 *    possible to lose.
 *  - It asks for nothing. The demo asks for a name, an email and a role when it
 *    starts, and says so; this bar does not pretend otherwise.
 */
const DISMISSED = 'persivo.ask.folded';
const PINNED = ['story', 'how', 'studio'];
const AWAY = ['pilot'];

// the questions it types, one after another, while it is waiting to be asked something
const PROMPTS = ['What happens after signup?', 'Is it secure for our team?', 'Can I see it configured for us?', 'How do I set it up?'];

// what opens when you reach for it: the question, and the opener written for it (see OPENERS in demo.ts)
const SUGGESTIONS = [
  { label: 'Show me what it does', purpose: 'Evaluating it' },
  { label: 'Is it secure for our team?', purpose: 'Security review' },
  { label: 'What does setup take?', purpose: 'Admin setup' },
  { label: 'See it configured for us', purpose: 'Configure it for us' },
] as const;

const isMac = typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);

/** The placeholder, typed and erased, while the field is empty and untouched. */
function useTypedPrompt(active: boolean) {
  const [text, setText] = useState(PROMPTS[0]!);
  useEffect(() => {
    if (!active) { setText(PROMPTS[0]!); return; }
    let i = 0, n = 0, timer = 0, mode: 'type' | 'hold' | 'erase' = 'type';
    const step = () => {
      const full = PROMPTS[i]!;
      if (mode === 'type') { n += 1; setText(full.slice(0, n)); if (n >= full.length) { mode = 'hold'; timer = window.setTimeout(step, 2300); return; } timer = window.setTimeout(step, 42); }
      else if (mode === 'hold') { mode = 'erase'; timer = window.setTimeout(step, 20); }
      else { n -= 1; setText(full.slice(0, Math.max(0, n))); if (n <= 0) { i = (i + 1) % PROMPTS.length; mode = 'type'; timer = window.setTimeout(step, 420); return; } timer = window.setTimeout(step, 20); }
    };
    setText(''); timer = window.setTimeout(step, 700);
    return () => clearTimeout(timer);
  }, [active]);
  return text;
}

export function DemoDock() {
  const { open, warm, live } = useDemo();
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [away, setAway] = useState(false);
  const [folded, setFolded] = useState(() => { try { return sessionStorage.getItem(DISMISSED) === '1'; } catch { return false; } });
  const [reach, setReach] = useState(false);          // opened on purpose — by a click or a key — and stays open until you click away or press Escape
  const [hover, setHover] = useState(false);
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  // earned: a few seconds, or the first real scroll — whichever comes first
  useEffect(() => {
    const reveal = () => setShown(true);
    const onScroll = () => { if (scrollY > innerHeight * 0.2) reveal(); };
    const timer = window.setTimeout(reveal, 4000);
    addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => { clearTimeout(timer); removeEventListener('scroll', onScroll); };
  }, []);

  // where it steps aside, and where it folds
  useEffect(() => {
    const watch = (ids: string[], set: (v: boolean) => void) => {
      const targets = ids.map(id => document.getElementById(id)).filter((t): t is HTMLElement => !!t);
      if (!targets.length) return () => {};
      const on = new Set<Element>();
      const io = new IntersectionObserver(entries => {
        for (const e of entries) { if (e.isIntersecting) on.add(e.target); else on.delete(e.target); }
        set(on.size > 0);
      }, { threshold: 0.08 });
      targets.forEach(t => io.observe(t));
      return () => io.disconnect();
    };
    const a = watch(PINNED, setPinned), b = watch(AWAY, setAway);
    return () => { a(); b(); };
  }, []);

  // ⌘K / Ctrl+K and "/" reach it from anywhere
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest?.('input,textarea,select,[contenteditable=true]');
      const hotkey = (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey);
      if (!hotkey || live) return;
      e.preventDefault();
      setShown(true); setReach(true);
      window.setTimeout(() => input.current?.focus(), 40);
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [live]);

  // A popover opened on purpose closes the way a popover does: a click outside it, or Escape. It does *not* close when
  // the input loses focus — pressing a suggestion takes focus first, and folding the bar under the pointer before the
  // click lands was exactly why a suggestion did nothing while it was folded.
  useEffect(() => {
    if (!reach) return;
    const down = (e: PointerEvent) => { if (!root.current?.contains(e.target as Node)) setReach(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { setReach(false); input.current?.blur(); } };
    addEventListener('pointerdown', down);
    addEventListener('keydown', esc);
    return () => { removeEventListener('pointerdown', down); removeEventListener('keydown', esc); };
  }, [reach]);

  const typedPrompt = useTypedPrompt(shown && !focused && !value && !reduced && !live);
  const compact = (folded || pinned) && !reach;
  const panelOpen = reach || focused || (hover && !compact);
  const up = shown && !live && !away;

  const start = (args: { purpose?: string; ask?: string }) => {
    open({ ...args, from: root.current });
    setValue(''); setReach(false); input.current?.blur();
  };
  const reachIn = () => { setReach(true); window.setTimeout(() => input.current?.focus(), 40); };
  const fold = () => { setFolded(true); setReach(false); input.current?.blur(); try { sessionStorage.setItem(DISMISSED, '1'); } catch { /* private window — it just stays unfolded next time */ } };

  return (
    <div ref={root} className={`ask${up ? ' up' : ''}${compact ? ' compact' : ''}${value ? ' has-value' : ''}`} aria-hidden={!up}
      data-open={panelOpen ? '' : undefined} data-focus={focused ? '' : undefined}
      onPointerEnter={() => { warm(); setHover(true); }} onPointerLeave={() => setHover(false)} onFocus={warm}>
      <div className="ask-card">
        {/* what opens when you reach for it */}
        <div className="ask-panel">
          <div className="ask-panel-in">
            <p className="ask-label">Try asking</p>
            <div className="ask-chips">
              {SUGGESTIONS.map(s => <button key={s.label} type="button" tabIndex={up && !compact ? 0 : -1} onClick={() => start({ purpose: s.purpose })}>{s.label}<span aria-hidden="true">↗</span></button>)}
            </div>
            <p className="ask-fine">A live guide, on the product itself. Nothing to install. It starts with your name, email and role, so the team can write back.</p>
          </div>
        </div>

        <form className="ask-bar" role="search" onClick={() => { if (compact) reachIn(); }} onSubmit={e => { e.preventDefault(); start({ ask: value }); }}>
          <button type="button" className="ask-orb" tabIndex={compact && up ? 0 : -1} aria-label="Ask Persivo"
            ><i/><i/><i/></button>
          <label className="ask-field">
            <span className="sr-only">Ask Persivo anything</span>
            <input ref={input} value={value} maxLength={240} autoComplete="off" spellCheck={false} enterKeyHint="go" tabIndex={up && !compact ? 0 : -1}
              placeholder={focused ? 'Ask anything about Persivo…' : ''}
              onChange={e => setValue(e.target.value)} onFocus={() => { setFocused(true); setReach(true); }} onBlur={() => setFocused(false)}
              onKeyDown={e => { if (e.key === 'Escape') setValue(''); }} />
            {!value && !focused && <span className="ask-ph" aria-hidden="true">{reduced ? PROMPTS[0] : typedPrompt}<i className="ask-caret"/></span>}
          </label>
          <span className="ask-compact-label" aria-hidden={!compact}>Ask Persivo</span>
          <kbd className="ask-kbd" aria-hidden="true">{isMac ? '⌘' : 'Ctrl'}<span>K</span></kbd>
          <button type="submit" className="ask-send" tabIndex={up && !compact ? 0 : -1} aria-label={value ? 'Start the tour with your question' : 'Start the tour'}>
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 13V3M3.5 7.5 8 3l4.5 4.5"/></svg>
          </button>
        </form>
      </div>
      <button type="button" className="ask-x" onClick={fold} tabIndex={up && !compact ? 0 : -1} title="Fold away" aria-label="Fold the Ask bar away">
        <svg width="10" height="10" viewBox="0 0 11 11" aria-hidden="true"><path d="M1.5 1.5l8 8M9.5 1.5l-8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
      </button>
    </div>
  );
}
